// UserManagement/index.tsx
import React, { useState, useMemo } from 'react';
import { Button, Box, TextField, InputAdornment } from '@mui/material';
import { Search } from '@mui/icons-material';
import { Plus } from 'lucide-react';

import PageHeader from '@/shared/components/PageHeader';
import { cx } from '@/styles/muiTheme';

import { InviteUserDialog } from './dialogs/InviteUserDialog';
import { InvitedUsersSection } from './components/InvitedUsersSection';
import { OrganizationMembersSection } from './components/OrganizationMembersSection';
import { DefaultRoleSection } from './components/DefaultRoleSection';
import { EditUserDialog } from './dialogs/EditUserDialog';
import { DisableUserDialog } from './dialogs/DisableUserDialog';
import { DeleteUserDialog } from './dialogs/DeleteUserDialog';
import { useUserManagement } from './hooks/useUserManagement';
import useCurrentOrgState from '@/shared/hooks/useCurrentOrg';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';

type Props = {
  /** Page title and subtitle, rendered through the shared PageHeader.
   *
   * Passed down rather than owned here so the page keeps its copy in one place,
   * while the header's action button stays next to the handler that opens the
   * invite dialog — that handler comes from useUserManagement below, which the
   * page has no access to. */
  title: string;
  subtitle?: string;
};

const UserManagement: React.FC<Props> = ({ title, subtitle }) => {
  const {
    state,
    handlers,
    mutations,
    groups,
    currentOrg,
    isLoading: userStateIsLoading,
  } = useUserManagement();

  const { departments, isLoading: orgStateIsLoading } = useCurrentOrgState();
  const [searchEmail, setSearchEmail] = useState('');

  // Filter users and invites by email search
  const filteredInvitedUsers = useMemo(() => {
    if (!searchEmail) return state.invitedUsers;
    const searchLower = searchEmail.toLowerCase();
    return state.invitedUsers.filter(user =>
      user.email?.toLowerCase().includes(searchLower)
    );
  }, [state.invitedUsers, searchEmail]);

  const filteredUsers = useMemo(() => {
    if (!searchEmail) return state.users;
    const searchLower = searchEmail.toLowerCase();
    return state.users.filter(user =>
      user.email?.toLowerCase().includes(searchLower)
    );
  }, [state.users, searchEmail]);

  if (userStateIsLoading || orgStateIsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle}
        action={
          <Button
            variant="contained"
            color="primary"
            onClick={() => handlers.setInviteOpen(true)}
            endIcon={<Plus />}
          >
            Invite User
          </Button>
        }
      />

      {/* Search alone now that Invite has moved to the header, so the row no
          longer needs space-between. The magnifier moved inside the field: it
          used to sit outside, which read as detached once the field gained an
          outlined border. */}
      <Box sx={{ maxWidth: 400, mb: 3 }}>
        <TextField
          fullWidth
          label="Search by email"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          placeholder="Enter email to filter users..."
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 16, color: cx.text.tertiary }} />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <InvitedUsersSection
        invitedUsers={filteredInvitedUsers}
        onEditUser={handlers.handleEditUserGroup}
        onDeleteUser={handlers.handleDelete}
        onDisableUser={handlers.handleDisable}
        onInviteUser={handlers.handleInvite}
      />

      <OrganizationMembersSection
        users={filteredUsers}
        onEditUser={handlers.handleEditUserGroup}
        onDeleteUser={handlers.handleDelete}
        onDisableUser={handlers.handleDisable}
      />

      <DefaultRoleSection
        currentOrg={currentOrg}
        groups={groups}
        onDefaultRoleChange={handlers.setDefaultRole}
      />

      <EditUserDialog
        //@ts-expect-error
        departments={departments}
        open={state.editDialogOpen}
        user={state.selectedUser}
        groupId={state.groupId}
        groups={groups}
        onClose={handlers.handleCancelEdit}
        onSave={handlers.handleUpdateUser}
        onDepartmentsChange={handlers.setUserDepartments}
    
        onGroupChange={handlers.setGroup}
        loading={mutations.inviteUserToOrganizationMutation.status === 'pending'}
      />

      <DisableUserDialog
        open={state.disableDialogOpen}
        user={state.selectedUser}
        onClose={() => handlers.setDisableDialogOpen(false)}
        onConfirm={handlers.confirmAccessToggle}
        disabled={state.confirmToggleDisabled}
      />

      <DeleteUserDialog
        open={state.deleteDialogOpen}
        user={state.selectedUser}
        onClose={() => handlers.setDeleteDialogOpen(false)}
        onConfirm={handlers.confirmDelete}
      />

      <InviteUserDialog
        open={state.inviteOpen}
        groups={groups}
        groupId={state.groupId}
        departments={departments ?? []}
        departmentId={state.inviteDepartmentId}
        onClose={() => handlers.setInviteOpen(false)}
        onInvite={handlers.handleInvite}
        onEmailsChange={handlers.setInviteEmails}
        onGroupChange={handlers.setGroup}
        onDepartmentChange={handlers.setInviteDepartment}
        loading={mutations.inviteUserToOrganizationMutation.status === 'pending'}
        emails={state.inviteEmails}
      />
    </>
  );
};

export default UserManagement;
